import { useMemo } from 'react';
import { cssVar } from '../lib/cssVar';
import { useThemeStore } from '../lib/theme';

/**
 * Builds a Clerk `appearance` object from the current CSS design tokens.
 *
 * Why a hook: Clerk's appearance API accepts hex/rgba strings, not CSS
 * variables. We resolve tokens at render time (after the stylesheet is parsed)
 * via getComputedStyle.
 *
 * Theme-aware (batch 2): the result is memoized on `themeVersion` from the
 * theme store, which `applyTheme` bumps on every theme change — a user pick,
 * the boot reconcile, or a forced theme (useForcedTheme on unauth/auth
 * surfaces). So when a signed-out visitor lands on the forced-Daylight
 * /sign-in page, the memo re-runs and the Clerk card re-reads the light-theme
 * token values (dark text on a light card) instead of the dark palette.
 *
 * Fallbacks: if a token resolves to empty string (stylesheet not yet parsed,
 * or a typo) we fall back to the resolved hex/rgba literal with a warning, so
 * the auth UI never renders unthemed. Those fallbacks — and the handful of
 * rgba() channels of a `--color-*` token that CSS cannot expand inside rgba()
 * — are tagged `theme-audit` inline (the batch-2 replacement for this file's
 * former whole-file allowlist deferral).
 *
 * Byte-parity rule (batch-2 hard constraint): under the midnight base (no
 * `data-theme` attribute) EVERY emitted value is byte-identical to this hook's
 * pre-batch-2 output — the structural border / white-alpha literals below are
 * emitted verbatim via `themed()`, not re-derived, so the flag-off render
 * cannot drift even below pixel-diff thresholds. Only non-midnight themes
 * re-derive those values from theme-aware tokens (`--color-border`,
 * `--hairline*`). The accent rgba() focus rings stay fixed on every theme (CSS
 * can't expand a var inside rgba() channels), which is consistent with the
 * rule: midnight output is 100% pre-batch-2-identical; non-midnight is
 * theme-aware wherever a token exists.
 */
export const useClerkAppearance = () => {
  // Subscribe to the theme-change counter so this component re-renders (and the
  // memo below re-derives) whenever the effective theme changes.
  const themeVersion = useThemeStore((s) => s.themeVersion);

  return useMemo(() => {
    // Reference themeVersion so the memo's dependency is honestly "used".
    void themeVersion;

    const get = (token: string, fallback: string) => {
      const v = cssVar(token);
      if (!v) {
        console.warn(`[useClerkAppearance] CSS variable ${token} resolved to empty; using fallback ${fallback}`);
        return fallback;
      }
      return v;
    };

    // Effective theme = the DOM attribute. applyTheme toggles it BEFORE
    // bumping themeVersion, and forced themes (useForcedTheme) never touch
    // store.theme — so the attribute, not the store, is the source of truth
    // here. No attribute → midnight base.
    const isMidnight =
      typeof document === 'undefined' ||
      !document.documentElement.hasAttribute('data-theme');

    // Midnight → the exact pre-batch-2 literal (byte-parity rule, header);
    // any other theme → the theme-aware token read. The read is a thunk so
    // midnight never resolves (or warns about) tokens it doesn't use.
    const themed = (tokenValue: () => string, midnightLiteral: string) =>
      isMidnight ? midnightLiteral : tokenValue();

    return {
      variables: {
        colorPrimary: get('--color-gold', '#7a9ab5'), // theme-audit: hex fallback for token read
        colorBackground: get('--color-cosmic-surface', '#161620'), // theme-audit: hex fallback for token read
        colorInputBackground: get('--color-surface-raised', '#1e1530'), // theme-audit: hex fallback for token read
        colorInputText: get('--color-text-primary', '#e8e2d9'), // theme-audit: hex fallback for token read
        colorText: get('--color-text-primary', '#e8e2d9'), // theme-audit: hex fallback for token read
        colorTextSecondary: get('--color-text-secondary', '#b0a3c0'), // theme-audit: hex fallback for token read
        colorTextOnPrimaryBackground: get('--color-cosmic-black', '#0d0d12'), // theme-audit: hex fallback for token read
        colorDanger: get('--color-rose', '#d4537e'), // theme-audit: hex fallback for token read
        colorSuccess: get('--color-teal', '#1d9e75'), // theme-audit: hex fallback for token read
        colorNeutral: get('--color-text-muted', '#8a7da0'), // theme-audit: hex fallback for token read
        borderRadius: '8px',
        fontFamily: get('--font-body', '"Inter Tight", sans-serif'),
        fontFamilyButtons: get('--font-display', '"Crimson Pro", serif'),
      },
      elements: {
        card: {
          backgroundColor: get('--color-cosmic-surface', '#161620'), // theme-audit: hex fallback for token read
          border: themed(() => `1px solid ${get('--color-border', '#2e2245')}`, '1px solid rgba(46, 34, 69, 0.8)'), // theme-audit: midnight byte-parity — original pre-token literal; hex fallback for token read
          boxShadow: '0 0 40px rgba(108, 63, 160, 0.15), 0 25px 50px rgba(0,0,0,0.5)', // theme-audit: mystic-purple-deep @ .15 + black scrim; CSS vars can't expand inside rgba()
        },
        headerTitle: {
          fontFamily: get('--font-display', '"Crimson Pro", serif'),
          color: get('--color-gold', '#7a9ab5'), // theme-audit: hex fallback for token read
          letterSpacing: '0.06em',
        },
        headerSubtitle: {
          color: get('--color-text-secondary', '#b0a3c0'), // theme-audit: hex fallback for token read
        },
        formButtonPrimary: {
          backgroundColor: get('--color-deep-purple', '#1a0d2e'), // theme-audit: hex fallback for token read
          border: `1px solid rgba(212, 168, 70, 0.3)`, // theme-audit: gold @ .3 alpha; CSS var can't expand inside rgba()
          color: get('--color-gold', '#7a9ab5'), // theme-audit: hex fallback for token read
          fontWeight: '500',
          letterSpacing: '0.05em',
          '&:hover': {
            backgroundColor: get('--color-mystic-purple', '#2a1b3d'), // theme-audit: hex fallback for token read
            borderColor: 'rgba(212, 168, 70, 0.5)', // theme-audit: gold @ .5 alpha; CSS var can't expand inside rgba()
          },
        },
        formFieldInput: {
          backgroundColor: get('--color-surface-raised', '#1e1530'), // theme-audit: hex fallback for token read
          borderColor: themed(() => get('--color-border', '#2e2245'), 'rgba(46, 34, 69, 0.8)'), // theme-audit: midnight byte-parity — original pre-token literal; hex fallback for token read
          color: get('--color-text-primary', '#e8e2d9'), // theme-audit: hex fallback for token read
          '&::placeholder': {
            color: get('--color-text-muted', '#8a7da0'), // theme-audit: hex fallback for token read
          },
          '&:focus': {
            borderColor: 'rgba(108, 63, 160, 0.6)', // theme-audit: mystic-purple-deep @ .6; CSS var can't expand inside rgba()
            boxShadow: '0 0 0 1px rgba(108, 63, 160, 0.3)', // theme-audit: mystic-purple-deep @ .3; CSS var can't expand inside rgba()
          },
        },
        formFieldLabel: {
          color: get('--color-text-secondary', '#b0a3c0'), // theme-audit: hex fallback for token read
        },
        otpCodeFieldInput: {
          backgroundColor: get('--color-surface-raised', '#1e1530'), // theme-audit: hex fallback for token read
          borderColor: 'rgba(212, 168, 70, 0.35)', // theme-audit: gold @ .35 alpha; CSS var can't expand inside rgba()
          color: get('--color-text-primary', '#e8e2d9'), // theme-audit: hex fallback for token read
          fontSize: '1.35rem',
          fontWeight: '500',
          caretColor: get('--color-gold', '#7a9ab5'), // theme-audit: hex fallback for token read
          '&:focus': {
            borderColor: 'rgba(212, 168, 70, 0.7)', // theme-audit: gold @ .7 alpha; CSS var can't expand inside rgba()
            boxShadow: '0 0 0 1px rgba(212, 168, 70, 0.35)', // theme-audit: gold @ .35 alpha; CSS var can't expand inside rgba()
          },
        },
        socialButtonsBlockButton: {
          backgroundColor: themed(() => get('--hairline-faint', 'rgba(255, 255, 255, 0.04)'), 'rgba(255, 255, 255, 0.04)'), // theme-audit: midnight byte-parity — original pre-token literal; theme-aware faint tint elsewhere
          border: themed(() => `1px solid ${get('--hairline', 'rgba(255, 255, 255, 0.07)')}`, '1px solid rgba(255, 255, 255, 0.08)'), // theme-audit: midnight byte-parity — original pre-token literal; theme-aware hairline elsewhere
          color: get('--color-text-primary', '#e8e2d9'), // theme-audit: hex fallback for token read
          '&:hover': {
            backgroundColor: themed(() => get('--hairline', 'rgba(255, 255, 255, 0.07)'), 'rgba(255, 255, 255, 0.08)'), // theme-audit: midnight byte-parity — original pre-token literal; theme-aware hairline elsewhere
          },
        },
        dividerLine: {
          backgroundColor: themed(() => get('--color-border', '#2e2245'), 'rgba(46, 34, 69, 0.6)'), // theme-audit: midnight byte-parity — original pre-token literal; theme-aware border elsewhere
        },
        dividerText: {
          color: get('--color-text-muted', '#8a7da0'), // theme-audit: hex fallback for token read
        },
        footerActionLink: {
          color: get('--color-gold', '#7a9ab5'), // theme-audit: hex fallback for token read
          '&:hover': {
            color: get('--color-gold', '#7a9ab5'), // theme-audit: hex fallback for token read
          },
        },
        footerActionText: {
          color: get('--color-text-muted', '#8a7da0'), // theme-audit: hex fallback for token read
        },
        footer: {
          '& + div': {
            background: get('--color-cosmic-surface', '#161620'), // theme-audit: hex fallback for token read
          },
        },
        internal: {
          color: get('--color-text-muted', '#8a7da0'), // theme-audit: hex fallback for token read
        },
        userButtonPopoverCard: {
          backgroundColor: get('--color-cosmic-surface', '#161620'), // theme-audit: hex fallback for token read
          border: themed(() => `1px solid ${get('--color-border', '#2e2245')}`, '1px solid rgba(46, 34, 69, 0.8)'), // theme-audit: midnight byte-parity — original pre-token literal; hex fallback for token read
          boxShadow: '0 0 30px rgba(108, 63, 160, 0.15), 0 20px 40px rgba(0,0,0,0.5)', // theme-audit: mystic-purple-deep @ .15 + black scrim; CSS vars can't expand inside rgba()
        },
        userButtonPopoverMain: {
          backgroundColor: get('--color-cosmic-surface', '#161620'), // theme-audit: hex fallback for token read
        },
        userButtonPopoverFooter: {
          backgroundColor: get('--color-cosmic-surface', '#161620'), // theme-audit: hex fallback for token read
          borderTop: themed(() => `1px solid ${get('--color-border', '#2e2245')}`, '1px solid rgba(46, 34, 69, 0.6)'), // theme-audit: midnight byte-parity — original pre-token literal; hex fallback for token read
        },
        userButtonPopoverActionButton: {
          color: get('--color-text-primary', '#e8e2d9'), // theme-audit: hex fallback for token read
          '&:hover': {
            backgroundColor: themed(() => get('--hairline', 'rgba(255, 255, 255, 0.06)'), 'rgba(255, 255, 255, 0.06)'), // theme-audit: midnight byte-parity — original pre-token literal; theme-aware hairline elsewhere
          },
        },
        userButtonPopoverActionButtonIcon: {
          color: get('--color-text-secondary', '#b0a3c0'), // theme-audit: hex fallback for token read
        },
        userButtonPopoverActionButtonText: {
          color: get('--color-text-primary', '#e8e2d9'), // theme-audit: hex fallback for token read
        },
        // Clerk styles CUSTOM menu items (<UserButton.Link>) from a separate
        // element key, and its default is a muted colour at 62% alpha — measured
        // at 2.11:1 on the light themes, which fails WCAG AA and reads as
        // "disabled". Nothing here overrode it, so every custom item in the menu
        // looked inert next to Clerk's own rows at 13.9:1. Mirror the action-button
        // treatment so our own destinations are as legible as Clerk's.
        // Icons inherit currentColor, so the button colour covers them too.
        userButtonPopoverCustomItemButton: {
          color: get('--color-text-primary', '#e8e2d9'), // theme-audit: hex fallback for token read
          '&:hover': {
            backgroundColor: themed(() => get('--hairline', 'rgba(255, 255, 255, 0.06)'), 'rgba(255, 255, 255, 0.06)'), // theme-audit: mirrors userButtonPopoverActionButton hover
          },
        },
        userButtonPopoverCustomItemButtonIconBox: {
          color: get('--color-text-secondary', '#b0a3c0'), // theme-audit: hex fallback for token read
        },
        userPreviewMainIdentifier: {
          color: get('--color-gold', '#7a9ab5'), // theme-audit: hex fallback for token read
        },
        userPreviewSecondaryIdentifier: {
          color: get('--color-text-secondary', '#b0a3c0'), // theme-audit: hex fallback for token read
        },
      },
    };
  }, [themeVersion]);
};
